import { EventRecurrenceType, EventRecurrenceTypeValues } from './event.model';

/** Fields needed to describe when an event happens; satisfied by both `EventDto` and `EventListEntry`. */
export type EventTimeFields = {
  isAllDay: boolean;
  startTime: string | null;
  endTime: string | null;
};

export type EventRecurrenceFields = {
  recurrenceType: EventRecurrenceTypeValues | string | number | null | undefined;
  recurrenceDaysOfWeek: string[] | null;
  recurrenceDayOfMonth: number | null;
  recurrenceWeekOfMonth: number | null;
  recurrenceDayOfWeekInTheMonth: string | null;
};

export const RECURRENCE_LABELS: Record<EventRecurrenceTypeValues, string> = {
  [EventRecurrenceType.None]: $localize`:@@EVENT_RECURRENCE_NONE:None`,
  [EventRecurrenceType.Daily]: $localize`:@@EVENT_RECURRENCE_DAILY:Daily`,
  [EventRecurrenceType.Weekly]: $localize`:@@EVENT_RECURRENCE_WEEKLY:Weekly`,
  [EventRecurrenceType.Monthly]: $localize`:@@EVENT_RECURRENCE_MONTHLY:Monthly`,
  [EventRecurrenceType.Yearly]: $localize`:@@EVENT_RECURRENCE_YEARLY:Yearly`
};

const DAY_ORDER = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function normalizeRecurrenceType(value: unknown): EventRecurrenceTypeValues {
  if (typeof value === 'number') return value as EventRecurrenceTypeValues;
  if (typeof value === 'string' && value.trim() !== '') {
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber)) return asNumber as EventRecurrenceTypeValues;
    const byName = (EventRecurrenceType as Record<string, number>)[value];
    if (byName !== undefined) return byName as EventRecurrenceTypeValues;
  }
  return EventRecurrenceType.None;
}

export function isRecurringEvent(recurrenceType: EventRecurrenceTypeValues): boolean {
  return recurrenceType !== EventRecurrenceType.None;
}

function ordinalLabel(week: number): string {
  switch (week) {
    case 1:
      return $localize`:@@EVENT_ORDINAL_FIRST:1st`;
    case 2:
      return $localize`:@@EVENT_ORDINAL_SECOND:2nd`;
    case 3:
      return $localize`:@@EVENT_ORDINAL_THIRD:3rd`;
    case 4:
      return $localize`:@@EVENT_ORDINAL_FOURTH:4th`;
    default:
      return $localize`:@@EVENT_ORDINAL_LAST:last`;
  }
}

/** "Weekly on: Mon, Tue", "Monthly on: 3rd Fri", "Daily", ... */
export function recurrenceLabel(
  recurrenceType: EventRecurrenceTypeValues,
  daysOfWeek: string[] | null,
  dayOfMonth: number | null,
  weekOfMonth: number | null,
  dayOfWeekInMonth: string | null
): string {
  const base = RECURRENCE_LABELS[recurrenceType] ?? RECURRENCE_LABELS[EventRecurrenceType.None];
  const on = $localize`:@@EVENT_RECURRENCE_ON:on:`;
  if (recurrenceType === EventRecurrenceType.Weekly && daysOfWeek?.length) {
    const days = [...daysOfWeek].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)).map((day) => day.slice(0, 3));
    return `${base} ${on} ${days.join(', ')}`;
  }
  if (recurrenceType === EventRecurrenceType.Monthly) {
    if (dayOfMonth) {
      return `${base} ${on} ${dayOfMonth}`;
    }
    if (weekOfMonth && dayOfWeekInMonth) {
      return `${base} ${on} ${ordinalLabel(weekOfMonth)} ${dayOfWeekInMonth.slice(0, 3)}`;
    }
  }
  return base;
}

/** Same as {@link recurrenceLabel} but reads the fields from a DTO / list entry. */
export function eventRecurrenceLabel(event: EventRecurrenceFields): string {
  return recurrenceLabel(
    normalizeRecurrenceType(event.recurrenceType),
    event.recurrenceDaysOfWeek,
    event.recurrenceDayOfMonth,
    event.recurrenceWeekOfMonth,
    event.recurrenceDayOfWeekInTheMonth
  );
}

/** "HH:mm" -> "h:mm AM/PM" */
export function formatTime(time: string | null | undefined): string {
  if (!time) {
    return '';
  }
  const [hourPart, minutePart] = time.split(':');
  const hours = Number(hourPart);
  if (Number.isNaN(hours)) {
    return '';
  }
  const minutes = minutePart ?? '00';
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${minutes} ${period}`;
}

/** "12:00 PM - 1:00 PM" or "All day(s)" */
export function timeRangeLabel(entry: EventTimeFields): string {
  if (entry.isAllDay) {
    return $localize`:@@EVENT_ALL_DAY_LABEL:All day(s)`;
  }
  const start = formatTime(entry.startTime);
  const end = formatTime(entry.endTime);
  if (start && end) {
    return `${start} - ${end}`;
  }
  return start;
}
